import { createDate, format, getTimezone } from '@lingoda/dates';
import { ContextError } from '@lingoda/errors';
import { BrowserTimeSyncError } from '../error';
import { getBrowserTimeZone } from './getBrowserTimeZone';

const ERROR_PREFIX = 'The check browser sync time failed';

export const checkTime = async (fetchApiTime: () => Promise<string | undefined>) => {
    try {
        const apiRequestedAt = new Date();
        const apiTimeUTC = await fetchApiTime();
        const apiReceivedAt = new Date();

        if (!apiTimeUTC) {
            throw new ContextError(
                `${ERROR_PREFIX}: the result of fetchApiTime should be defined`,
                {
                    context: {
                        fetchApiTime: {
                            apiRequestedAt: apiRequestedAt.toISOString(),
                            apiTimeUTC,
                            apiReceivedAt: apiReceivedAt.toISOString(),
                        },
                    },
                },
            );
        }

        const data = getBrowserTimeOutOfSyncErrorData({
            apiTimeUTC,
            apiRequestedAt,
            apiReceivedAt,
            browserTime: apiReceivedAt,
        });

        if (!data.isTimeSync) {
            throw new BrowserTimeSyncError(
                `${ERROR_PREFIX}: time is out of sync with the API time.`,
                {
                    context: { data },
                },
            );
        }
    } catch (error) {
        if (error instanceof ContextError || error instanceof BrowserTimeSyncError) {
            throw error;
        }

        throw new ContextError(
            `${ERROR_PREFIX}: ${error instanceof Error ? error.message : 'unknown error'}`,
            {
                context: { originalError: { message: String(error) } },
                cause: error,
            },
        );
    }
};

interface SyncErrorParams {
    apiTimeUTC: string;
    apiRequestedAt: Date;
    apiReceivedAt: Date;
    browserTime: Date;
}

export const getBrowserTimeOutOfSyncErrorData = ({
    apiTimeUTC,
    apiRequestedAt,
    apiReceivedAt,
    browserTime,
}: SyncErrorParams) => {
    const userDefaultTimezone = getTimezone();
    const userTimeZoneOffset = format('OOOO', createDate());

    const browserApiTimeDifferenceMs = Math.abs(
        browserTime.getTime() - new Date(apiTimeUTC).getTime(),
    );

    const isTimeSync = browserApiTimeDifferenceMs < MAX_TIME_DIFFERENCE_MS;

    const apiTimeFetchDifferenceMs = Math.abs(apiReceivedAt.getTime() - apiRequestedAt.getTime());

    const isApiTimeFetchTooLong = apiTimeFetchDifferenceMs > MAX_TIME_DIFFERENCE_MS;

    const data = {
        isTimeSync,
        MAX_TIME_DIFFERENCE_MS,
        browserTimeUTC: browserTime.toISOString(),
        apiTimeUTC,
        apiRequestedAtUTC: apiRequestedAt.toISOString(),
        apiReceivedAtUTC: apiReceivedAt.toISOString(),
        isApiTimeFetchTooLong,
        apiTimeFetchDifferenceMs,
        browserApiTimeDifferenceMs,
        browserTimeZoneFixed: getBrowserTimeZone(),
        getBrowserTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        userDate: createDate().toString(),
        userDefaultTimezone,
        userTimeZoneOffset,
    };

    return data;
};

const MAX_TIME_DIFFERENCE_MS = 5 * 60 * 1000;
export const TRYING_TO_GENERATE_ZOOM_MEETING_TOO_EARLY_ERROR_MESSAGE =
    'Trying to generate Zoom meeting too early';
